import { useState } from 'react'
import type { FeedItem } from '../../lib/feed'
import { moderateContent, type FeedModerationAction } from '../../lib/moderation'
import { messageOf } from '../../lib/errors'
import type { CheckNote as Note } from '../../lib/moderation'
import { CheckNote } from './CheckNote'

/**
 * Moderator controls on a feed card — FED-7.
 *
 * ── IT IS OFF UNLESS THE MODERATOR ARMED IT ─────────────────────────────────
 *
 * This renders nothing at all unless moderator mode is on, which is off by
 * default on every page load. That is not timidity about clutter: remove is an
 * IRREVERSIBLE soft delete, and a moderator reads the hub daily like everybody
 * else. A delete button rendered on every card in a feed somebody scrolls with
 * their thumb is a mis-tap away from destroying a colleague's post, with no
 * undo and an audit row saying they meant it.
 *
 * Arming is a deliberate act, and the act is the point — it is the same
 * reasoning as `employeeView` next to it in Home, one notch more serious.
 *
 * ── WHY REMOVE ASKS FOR A NOTE AND HIDE DOES NOT ────────────────────────────
 *
 * Hide is reversible and is meant to be FAST — the whole reason it exists is
 * the 11pm Sunday case where the right move is to stop something spreading
 * before deciding anything. Putting a form in front of it defeats it. The
 * database writes a default reason, and unhide is one click away.
 *
 * Remove cannot be undone, so it gets the note and a confirm step. The note is
 * what FED-16's appeal will point at.
 *
 * ── THE LIST RELOADS; THE ROW IS NEVER RE-READ ──────────────────────────────
 *
 * onDone re-reads the feed rather than editing local state, and that is load
 * bearing for remove specifically. Invariant 16c measured it: once a post is
 * removed, the moderator who removed it CANNOT SEE THE ROW — content_is_visible
 * has no clause for a deleted post except the author's. Anything that read the
 * row back to confirm would get nothing and report a failure that did not
 * happen. Same family as 028c.
 */
export function PostModerationBar({
  post,
  onDone,
  checkNote,
}: {
  post: FeedItem
  /** Re-read the feed. Never splice — see the header. */
  onDone: () => void | Promise<void>
  /**
   * 127 (FED-25). What the check said about this post — the verdict and its
   * reasoning, which section 9 promises moderators. Undefined while it loads;
   * null when no verdict was ever recorded.
   */
  checkNote?: Note | null
}) {
  const [note, setNote] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hidden = post.hiddenAt !== null

  async function act(action: FeedModerationAction) {
    setBusy(true)
    setError(null)
    try {
      await moderateContent(post.contentId, action, note)
      setConfirming(false)
      setNote('')
      await onDone()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`modbar${hidden ? ' modbar--hidden' : ''}`}>
      <CheckNote note={checkNote} />
      {error && (
        <p className="modbar__error" role="alert">
          {error}
        </p>
      )}

      {hidden && (
        <p className="modbar__state">
          <strong>Hidden.</strong> Nobody but you and the author can see this.
          {post.hiddenReason && <> — {post.hiddenReason}</>}
        </p>
      )}

      {!confirming ? (
        <div className="modbar__actions">
          {hidden ? (
            <button type="button" className="btn btn--quiet" disabled={busy} onClick={() => act('unhide')}>
              {busy ? 'Working…' : 'Put it back'}
            </button>
          ) : (
            <button type="button" className="btn btn--quiet" disabled={busy} onClick={() => act('hide')}>
              {busy ? 'Working…' : 'Hide'}
            </button>
          )}
          <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={() => setConfirming(true)}>
            Remove
          </button>
          {/* Says what the pair is FOR. A moderator meeting these two buttons
              at speed should not have to remember which one is permanent. */}
          <span className="modbar__hint">
            Hide is reversible. Remove is not.
          </span>
        </div>
      ) : (
        <div className="modbar__confirm">
          <label>
            <span>Why are you removing this? The author and any appeal will see it.</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Removed after publication because…"
            />
          </label>
          <div className="modbar__confirmrow">
            <button type="button" className="btn btn--danger btn--sm" disabled={busy} onClick={() => act('remove')}>
              {busy ? 'Working…' : 'Remove permanently'}
            </button>
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() => {
                setConfirming(false)
                setNote('')
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
